// src/tattoo/MobileBottomDock.jsx
import React from "react";
import { useSnapshot } from "valtio";
import { tattoo, setMorph, setSkinColor } from "./state";
import { guidePaintRef } from "./GuidePaint";
import { addPaintLayerFromGP } from "./paintToLayer";
import { isSharedView } from "./sharedCommentsApi";
import { useState } from "react";
import {
  AudioWaveform, // rope-ish
  FlipHorizontal, // symmetry
  SquarePlus,
  RotateCcw,
  Trash2,
  Dumbbell,
  SlidersHorizontal,
} from "lucide-react";

import { ttSymIcon } from "../icons/ttSymIcon";

function hexToRgba(hex, alpha = 1) {
  const m =
    /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#ff0000") || [];
  const r = parseInt(m[1] || "ff", 16);
  const g = parseInt(m[2] || "00", 16);
  const b = parseInt(m[3] || "00", 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function MobileBottomDock({
  onOpenMorphs = null,
  onOpenSheet = null,
}) {
  const snap = useSnapshot(tattoo);
  const viewMode = isSharedView(); // true on /view links, false in /app
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState("paint"); // "paint" | "morphs"
  // symmetry toggle (mirror paint) – matches desktop/iPad behavior
  const [symmetryOn, setSymmetryOn] = React.useState(false);

  const [brush, setBrush] = React.useState(14);
  const [color, setColor] = React.useState("#ff0000");
  const [paintOn, setPaintOn] = React.useState(false);
  const [hasPaint, setHasPaint] = React.useState(false);

  /* ---------------- PAINT WIRING ---------------- */

  React.useEffect(() => {
    const computeHasPaint = () => {
      const gp = guidePaintRef.current;
      if (!gp) return false;

      // prefer hasStrokes/isEmpty if present
      if (typeof gp.hasStrokes === "function") return !!gp.hasStrokes();
      if (typeof gp.isEmpty === "function") return !gp.isEmpty();

      // fallback: unknown => false
      return false;
    };

    const sync = (e) => {
      // paint toggle state (source of truth is the event OR gp.enabled)
      if (e?.type === "guidepaint:toggle") {
        const on = !!(e?.detail?.on ?? guidePaintRef.current?.enabled);
        setPaintOn(on);
      } else {
        // stroke/cleared events: keep paintOn as-is
        // but still refresh hasPaint below
      }

      setHasPaint(computeHasPaint());
    };

    window.addEventListener("guidepaint:toggle", sync);
    window.addEventListener("guidepaint:stroke", sync);
    window.addEventListener("guidepaint:cleared", sync);

    // initial sync
    sync({ type: "init" });

    return () => {
      window.removeEventListener("guidepaint:toggle", sync);
      window.removeEventListener("guidepaint:stroke", sync);
      window.removeEventListener("guidepaint:cleared", sync);
    };
  }, []);

  React.useEffect(() => {
    const gp = guidePaintRef.current;
    if (!gp) return;

    try {
      const b = gp.getBrush?.() ?? gp.brush ?? 14;
      if (Number.isFinite(b)) setBrush(b);
    } catch {}

    try {
      const cur = gp.getColor?.() ?? gp.color;
      if (typeof cur === "string" && cur.startsWith("rgba")) {
        const m = cur.match(
          /rgba\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i
        );
        if (m) {
          const toHex = (n) =>
            Math.max(0, Math.min(255, Math.round(n)))
              .toString(16)
              .padStart(2, "0");
          const hx = `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`;
          setColor(hx);
        }
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    const gp = guidePaintRef.current;
    if (!gp) return;
    setSymmetryOn(!!gp.symmetry);
  }, []);

  const applyBrush = (value) => {
    const v = Number(value) || 1;
    setBrush(v);
    const gp = guidePaintRef.current;
    try {
      gp?.setBrush?.(v);
    } catch {}
  };

  const applyColor = (hex) => {
    setColor(hex);
    const gp = guidePaintRef.current;
    if (!gp) return;
    let alpha = 1;
    try {
      const cur = gp.getColor?.() ?? gp.color;
      const m =
        typeof cur === "string"
          ? cur.match(/rgba\([^,]+,[^,]+,[^,]+,\s*([0-9.]+)\)/i)
          : null;
      if (m) alpha = Math.max(0, Math.min(1, parseFloat(m[1])));
    } catch {}
    const rgba = hexToRgba(hex, alpha);
    try {
      gp.setColor?.(rgba);
      window.dispatchEvent(
        new CustomEvent("guidepaint:color", { detail: { color: rgba } })
      );
    } catch {}
  };

  const toggleOpen = () => setOpen((o) => !o);

  const turnOffPaint = () => {
    const gp = guidePaintRef.current;
    if (!gp) return;

    try {
      gp.setEnabled?.(false);
    } catch {}
    try {
      gp.setOn?.(false);
    } catch {}

    gp.enabled = false;
    gp._autoDisable = true;

    try {
      window.dispatchEvent(
        new CustomEvent("guidepaint:toggle", { detail: { on: false } })
      );
    } catch {}
  };

  const toggleSymmetry = () => {
    const gp = guidePaintRef.current;
    if (!gp) return;
    const next = !symmetryOn;
    try {
      gp.setSymmetry?.(next);
    } catch {}
    gp.symmetry = next; // fallback if method missing
    setSymmetryOn(next);
  };

  const handleAddPaintLayer = () => {
    try {
      const res = addPaintLayerFromGP?.();

      if (res && typeof res.then === "function") {
        res
          .then(() => {
            // ✅ after the paint is baked into a layer, clear the GP canvas
            // so "hasPaint" becomes false and the +layer button hides.
            try {
              guidePaintRef.current?.clear?.();
              window.dispatchEvent(new CustomEvent("guidepaint:cleared"));
            } catch (e) {
              console.warn("[mobile] guidepaint clear failed", e);
            }

            turnOffPaint();
          })
          .catch((err) => {
            console.warn("[mobile] addPaintLayerFromGP failed", err);
          });
      } else {
        // non-promise version: do the same cleanup
        try {
          guidePaintRef.current?.clear?.();
          window.dispatchEvent(new CustomEvent("guidepaint:cleared"));
        } catch (e) {
          console.warn("[mobile] guidepaint clear failed", e);
        }

        turnOffPaint();
      }
    } catch (err) {
      console.warn("[mobile] addPaintLayerFromGP threw", err);
    }
  };

  /* ---------------- MORPHS (DEDUPED BY NAME) ---------------- */

  // targets: [{ name: "Thin", slots: [{ mesh, index }, ...] }, ...]
  const [targets, setTargets] = React.useState([]);

  const isPoseMorph = (name) => /^POSE__/i.test(name);
  const collectMorphTargets = React.useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const roots = (window.__tattoo_roots || []).filter(Boolean);
      const map = Object.create(null);

      for (const root of roots) {
        root.traverse((node) => {
          if (
            !node ||
            !node.isMesh ||
            !node.morphTargetDictionary ||
            !node.morphTargetInfluences
          ) {
            return;
          }
          const dict = node.morphTargetDictionary;
          const infl = node.morphTargetInfluences;

          for (const [name, index] of Object.entries(dict)) {
            if (isPoseMorph(name)) continue; // ✅ hide POSE__ from Body Morphs
            if (
              typeof index !== "number" ||
              index < 0 ||
              index >= infl.length
            ) {
              continue;
            }
            const key = name; // collapse globally by name
            if (!map[key]) {
              map[key] = { name: key, slots: [] };
            }
            map[key].slots.push({ mesh: node, index });
          }
        });
      }

      setTargets(Object.values(map));
    } catch (e) {
      console.warn("[mobile-morphs] collect failed", e);
      setTargets([]);
    }
  }, []);

  React.useEffect(() => {
    collectMorphTargets();
    const timers = [200, 600, 1200].map((ms) =>
      setTimeout(collectMorphTargets, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [collectMorphTargets]);

  React.useEffect(() => {
    const recollect = () => collectMorphTargets();
    const rerender = () => setTargets((t) => t.slice());

    window.addEventListener("morph:collect", recollect);
    window.addEventListener("morph:changed", recollect);
    window.addEventListener("morph:changed", rerender);

    return () => {
      window.removeEventListener("morph:collect", recollect);
      window.removeEventListener("morph:changed", recollect);
      window.removeEventListener("morph:changed", rerender);
    };
  }, [collectMorphTargets]);

  const setInfluenceForEntry = (entry, value) => {
    try {
      for (const slot of entry.slots) {
        const mesh = slot.mesh;
        const index = slot.index;
        const infl = mesh?.morphTargetInfluences;
        if (!infl || index < 0 || index >= infl.length) continue;

        infl[index] = value;

        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        mats.forEach((m) => m && (m.needsUpdate = true));
      }

      // persist in tattoo.morphs using the raw glTF morph name
      setMorph(entry.name, value);

      try {
        window.dispatchEvent(
          new CustomEvent("morph:changed", {
            detail: { name: entry.name, value },
          })
        );
      } catch {}
    } catch (e) {
      console.warn("[mobile-morphs] setInfluence failed", e);
    }
  };
  const [ropeOn, setRopeOn] = useState(() => {
    try {
      return localStorage.getItem("tt_rope_enabled") === "1";
    } catch {
      return true;
    }
  });
  const currentValueForEntry = (entry) => {
    try {
      if (!entry.slots.length) return 0;
      let sum = 0;
      let count = 0;
      for (const slot of entry.slots) {
        const infl = slot.mesh?.morphTargetInfluences;
        if (!infl || slot.index < 0 || slot.index >= infl.length) continue;
        const v = infl[slot.index];
        if (Number.isFinite(v)) {
          sum += v;
          count++;
        }
      }
      return count ? sum / count : 0;
    } catch {
      return 0;
    }
  };

  const canAddPaintLayer = !viewMode && hasPaint;
  const canAttachSketch = viewMode && hasPaint;

  /* ---------------- RENDER ---------------- */
  const snapTattoo = useSnapshot(tattoo);
  return (
    <div
      className={`tt-mobile-dock ${open ? "is-open" : ""}`}
      // Make the full overlay click-through; only inner bits are interactive
      style={{ pointerEvents: "none" }}
    >
      {/* Top strip: paint controls + morphs toggle + chevron */}
      <div
        className="tt-mobile-dock-bar"
        // Bar should be interactive
        style={{ pointerEvents: "auto" }}
      >
        <div className="tt-mobile-dock-paint">
          <div
            className="tt-mobile-color-swatch"
            style={{ position: "relative" }}
            aria-label="Brush color"
            title="Brush color"
          >
            <span
              className="tt-mobile-color-inner"
              style={{ backgroundColor: color }}
            />
            <input
              type="color"
              value={color}
              onChange={(e) => applyColor(e.target.value)}
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                border: "none",
                padding: 0,
                margin: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer",
                background: "transparent",
                WebkitAppearance: "none",
              }}
            />
          </div>

          <div className="tt-mobile-brush-wrap">
            <span className="tt-mobile-brush-label">Brush</span>
            <input
              type="range"
              min={1}
              max={48}
              step={1}
              value={brush}
              onChange={(e) => applyBrush(e.target.value)}
              aria-label="Brush size"
            />
          </div>
          <span className="tt-mobile-brush-value">{brush}px</span>
          <button
            className={`rope-btn tt-iconbtn ${ropeOn ? "is-on" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              const gp = guidePaintRef.current;
              if (!gp) return;

              const next = !gp.isRopeEnabled?.(); // use gp as source of truth
              gp.setRopeEnabled?.(next);

              setRopeOn(next); // ✅ forces label update

              console.log("[gp] rope enabled =", next);
            }}
            title="Rope stroke"
            aria-pressed={ropeOn}
          >
            <AudioWaveform size={18} strokeWidth={2.25} />
          </button>

          {/* Symmetry toggle */}
          <button
            type="sym-button"
            className={`sym-btn tt-iconbtn ${symmetryOn ? "is-on" : ""}`}
            onClick={toggleSymmetry}
            title="Toggle symmetry"
            aria-pressed={symmetryOn}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: symmetryOn
                ? "1px solid #ffffff"
                : "1px solid rgba(255,255,255,0.25)",
              background: symmetryOn ? "#0d7845" : "rgba(20,20,20,0.9)",
              color: "#fff",
              fontSize: 11,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            <FlipHorizontal size={18} strokeWidth={2.25} />
          </button>

          {hasPaint && (
            <button
              type="button"
              className={`tt-iconbtn ${hasPaint ? "is-active" : ""}`}
              onClick={
                viewMode
                  ? () =>
                      window.dispatchEvent(
                        new Event("comments:attach-from-model")
                      )
                  : handleAddPaintLayer
              }
              title={
                viewMode ? "Attach sketch as comment" : "Add paint as layer"
              }
            >
              <SquarePlus size={18} strokeWidth={2.25} />
            </button>
          )}

          {/* Undo */}
          <button
            type="button"
            className="tt-btn tt-icon tt-iconbtn"
            onClick={() => {
              const gp = guidePaintRef.current;
              gp?.undo?.();
            }}
            title="Undo last stroke"
            style={{ fontSize: "12px", padding: "6px 10px", lineHeight: 1 }}
          >
            <RotateCcw size={18} strokeWidth={2.25} />
          </button>

          {/* Clear paint + disengage */}
          <button
            type="button"
            className="tt-btn tt-icon"
            onClick={() => {
              const gp = guidePaintRef.current;
              if (!gp) return;
              gp.clearAll?.();
              gp.clear?.();
              try {
                window.dispatchEvent(
                  new CustomEvent("guidepaint:cleared", { detail: {} })
                );
              } catch {}
              turnOffPaint();
            }}
            title="Clear paint"
            style={{ padding: "6px 10px", lineHeight: 1 }}
          >
            <Trash2 size={18} strokeWidth={2.25} />
          </button>
        </div>

        {/* Morphs tab toggle */}
       {/* <button
          type="button"
          className={`tt-btn move-icon ${
            mode === "morphs" && open ? "is-active" : ""
          }`}
          title={
            mode === "morphs" && open ? "Hide body morphs" : "Show body morphs"
          }
          onClick={() => {
            // NEW: morphs live in the design panel bottom sheet
            if (onOpenMorphs) {
              onOpenSheet?.(); // open the sheet (optional but feels right)
              onOpenMorphs(); // set panelMode="morphs"
              return;
            }

            // fallback (if not wired): keep old behavior
            if (mode === "morphs" && open) {
              setOpen(false);
              setMode("paint");
            } else {
              setMode("morphs");
              setOpen(true);
            }
          }}
        >
          <Dumbbell size={18} strokeWidth={2.25} />
        </button>*/}

        {/* Open/close chevron */}
        {/* (if you add a chevron button later, it will also be inside this bar, so it's interactive) */}
      </div>

      {/* Drawer */}

      <div
        className="tt-mobile-dock-panel"
        // Drawer should also be interactive
        style={{ pointerEvents: "auto" }}
      >
        {false && mode === "morphs" && (
          <div className="tt-mobile-panel-inner">
            <div
              style={{
                fontSize: 12,
                opacity: 0.8,
                marginBottom: 8,
                textTransform: "uppercase",
              }}
            >
              Body Morphs
            </div>

            {targets.length === 0 && (
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                No morph targets found on this model.
              </div>
            )}

            {targets.map((entry) => {
              const cur = currentValueForEntry(entry);
              return (
                <label
                  key={entry.name}
                  className="tt-mobile-morph-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    margin: "8px 0",
                  }}
                >
                  <span
                    style={{
                      flex: "0 0 44%",
                      fontSize: 12,
                      color: "#e4e4e4",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {entry.name.replace(/_/g, " ")}
                  </span>
                  <input
                    type="range"
                    className="tt-mobile-morph-slider"
                    min={0}
                    max={1}
                    step={0.01}
                    value={cur}
                    onChange={(ev) =>
                      setInfluenceForEntry(entry, parseFloat(ev.target.value))
                    }
                    onPointerDown={(ev) =>
                      ev.currentTarget.classList.add("is-touching")
                    }
                    onPointerUp={(ev) =>
                      ev.currentTarget.classList.remove("is-touching")
                    }
                    onPointerCancel={(ev) =>
                      ev.currentTarget.classList.remove("is-touching")
                    }
                    onPointerLeave={(ev) =>
                      ev.currentTarget.classList.remove("is-touching")
                    }
                    style={{ flex: 1 }}
                  />
                  <span
                    style={{
                      minWidth: 34,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 11,
                      opacity: 0.8,
                    }}
                  >
                    {cur.toFixed(2)}
                  </span>
                </label>
              );
            })}
            {/* --- Skin tone (opens/closes with Body Morphs) --- */}
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid #2b2b2b",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: "#e8e8e8" }}
                >
                  Skin tone
                </div>
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                  Pick a tone so the artist previews the tattoo on you.
                </div>
              </div>

              <input
                type="color"
                value={snapTattoo.skinColor || "#d19771"}
                onChange={(e) => setSkinColor(e.target.value)}
                title="Skin tone"
                style={{
                  width: 34,
                  height: 34,
                  padding: 0,
                  border: "1px solid #333",
                  borderRadius: 8,
                  background: "transparent",
                  cursor: "pointer",
                  opacity: 1,
                  left: 358,
                }}
              />
            </div>
          </div>
        )}

        {mode === "paint" && (
          <div className="tt-mobile-panel-inner">
            <div className="tt-mobile-placeholder">
              <p style={{ fontSize: 11, opacity: 0.7 }}>
                Painting uses the controls in the strip above. No extra panel
                needed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
